import {
  ConsolidateEducationDegreesRequest,
  ConsolidateEducationFacultiesRequest,
  ConsolidateEducationInstitutionsRequest,
  EducationDegree,
  EducationDegreeCreateRequest,
  EducationDegreeUpdateRequest,
  EducationFaculty,
  EducationFacultyCreateRequest,
  EducationFacultyUpdateRequest,
  EducationInstitution,
  EducationInstitutionCreateRequest,
  EducationInstitutionUpdateRequest,
} from "../../models/education.model";
import { BackendHttpService } from "../backendHttpService";

export class EducationDictionaryAdminService extends BackendHttpService {
  // Cache for each dictionary
  private degreesDictionaryCache: EducationDegree[] | null = null;
  private institutionsDictionaryCache: EducationInstitution[] | null = null;
  private facultiesDictionaryCache: EducationFaculty[] | null = null;

  constructor() {
    super("educationdictionaries");
  }

  // Clear specific cache when the corresponding dictionary changes
  private clearDegreesCache(): void {
    this.degreesDictionaryCache = null;
  }

  private clearInstitutionsCache(): void {
    this.institutionsDictionaryCache = null;
  }

  private clearFacultiesCache(): void {
    this.facultiesDictionaryCache = null;
  }

  // Education Degrees (Admin operations)
  async getEducationDegrees(): Promise<EducationDegree[]> {
    if (this.degreesDictionaryCache) {
      return this.degreesDictionaryCache;
    }
    try {
      this.degreesDictionaryCache = await this.get<EducationDegree[]>(
        "/degrees"
      );
      return this.degreesDictionaryCache;
    } catch (error) {
      console.error("Error getting education degrees:", error);
      throw error;
    }
  }

  async createEducationDegree(
    request: EducationDegreeCreateRequest
  ): Promise<EducationDegree> {
    try {
      const degree = await this.post<
        EducationDegreeCreateRequest,
        EducationDegree
      >(request, "/degrees");
      this.clearDegreesCache();
      return degree;
    } catch (error) {
      console.error("Error creating education degree:", error);
      throw error;
    }
  }

  async updateEducationDegree(
    degreeId: number,
    request: EducationDegreeUpdateRequest
  ): Promise<EducationDegree> {
    try {
      const degree = await this.put<
        EducationDegreeUpdateRequest,
        EducationDegree
      >(request, `/degrees/${degreeId}`);
      this.clearDegreesCache();
      return degree;
    } catch (error) {
      console.error("Error updating education degree:", error);
      throw error;
    }
  }

  async deleteEducationDegree(degreeId: number): Promise<void> {
    try {
      await this.delete(`/degrees/${degreeId}`);
      this.clearDegreesCache();
    } catch (error) {
      console.error("Error deleting education degree:", error);
      throw error;
    }
  }

  async consolidateEducationDegrees(
    request: ConsolidateEducationDegreesRequest
  ): Promise<EducationDegree> {
    try {
      const degree = await this.post<
        ConsolidateEducationDegreesRequest,
        EducationDegree
      >(request, "/degrees/consolidate");
      this.clearDegreesCache();
      return degree;
    } catch (error) {
      console.error("Error consolidating education degrees:", error);
      throw error;
    }
  }

  // Education Institutions (Admin operations)
  async getEducationInstitutions(): Promise<EducationInstitution[]> {
    if (this.institutionsDictionaryCache) {
      return this.institutionsDictionaryCache;
    }
    try {
      this.institutionsDictionaryCache = await this.get<EducationInstitution[]>(
        "/institutions"
      );
      return this.institutionsDictionaryCache;
    } catch (error) {
      console.error("Error getting education institutions:", error);
      throw error;
    }
  }

  async createEducationInstitution(
    request: EducationInstitutionCreateRequest
  ): Promise<EducationInstitution> {
    try {
      const institution = await this.post<
        EducationInstitutionCreateRequest,
        EducationInstitution
      >(request, "/institutions");
      this.clearInstitutionsCache();
      return institution;
    } catch (error) {
      console.error("Error creating education institution:", error);
      throw error;
    }
  }

  async updateEducationInstitution(
    institutionId: number,
    request: EducationInstitutionUpdateRequest
  ): Promise<EducationInstitution> {
    try {
      const institution = await this.put<
        EducationInstitutionUpdateRequest,
        EducationInstitution
      >(request, `/institutions/${institutionId}`);
      this.clearInstitutionsCache();
      return institution;
    } catch (error) {
      console.error("Error updating education institution:", error);
      throw error;
    }
  }

  async deleteEducationInstitution(institutionId: number): Promise<void> {
    try {
      await this.delete(`/institutions/${institutionId}`);
      this.clearInstitutionsCache();
    } catch (error) {
      console.error("Error deleting education institution:", error);
      throw error;
    }
  }

  async consolidateEducationInstitutions(
    request: ConsolidateEducationInstitutionsRequest
  ): Promise<EducationInstitution> {
    try {
      const institution = await this.post<
        ConsolidateEducationInstitutionsRequest,
        EducationInstitution
      >(request, "/institutions/consolidate");
      this.clearInstitutionsCache();
      return institution;
    } catch (error) {
      console.error("Error consolidating education institutions:", error);
      throw error;
    }
  }

  // Education Faculties (Admin operations)
  async getEducationFaculties(): Promise<EducationFaculty[]> {
    if (this.facultiesDictionaryCache) {
      return this.facultiesDictionaryCache;
    }
    try {
      this.facultiesDictionaryCache = await this.get<EducationFaculty[]>(
        "/faculties"
      );
      return this.facultiesDictionaryCache;
    } catch (error) {
      console.error("Error getting education faculties:", error);
      throw error;
    }
  }

  async createEducationFaculty(
    request: EducationFacultyCreateRequest
  ): Promise<EducationFaculty> {
    try {
      const faculty = await this.post<
        EducationFacultyCreateRequest,
        EducationFaculty
      >(request, "/faculties");
      this.clearFacultiesCache();
      return faculty;
    } catch (error) {
      console.error("Error creating education faculty:", error);
      throw error;
    }
  }

  async updateEducationFaculty(
    facultyId: number,
    request: EducationFacultyUpdateRequest
  ): Promise<EducationFaculty> {
    try {
      const faculty = await this.put<
        EducationFacultyUpdateRequest,
        EducationFaculty
      >(request, `/faculties/${facultyId}`);
      this.clearFacultiesCache();
      return faculty;
    } catch (error) {
      console.error("Error updating education faculty:", error);
      throw error;
    }
  }

  async deleteEducationFaculty(facultyId: number): Promise<void> {
    try {
      await this.delete(`/faculties/${facultyId}`);
      this.clearFacultiesCache();
    } catch (error) {
      console.error("Error deleting education faculty:", error);
      throw error;
    }
  }

  async consolidateEducationFaculties(
    request: ConsolidateEducationFacultiesRequest
  ): Promise<EducationFaculty> {
    try {
      const faculty = await this.post<
        ConsolidateEducationFacultiesRequest,
        EducationFaculty
      >(request, "/faculties/consolidate");
      this.clearFacultiesCache();
      return faculty;
    } catch (error) {
      console.error("Error consolidating education faculties:", error);
      throw error;
    }
  }
}

// Export singleton instance for convenience
export const educationDictionaryAdminService =
  new EducationDictionaryAdminService();
